import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import StatusBadge from '../components/StatusBadge';
import { ClipboardList, Clock, CheckCircle, Zap, AlertCircle, ChevronDown, ChevronUp, FileText, Mail } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const ReviewerDashboard = () => {
  const { user, token } = useAuth();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [approving, setApproving] = useState(null);

  const fetchSubmissions = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/submissions`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        setSubmissions(await res.json());
      }
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchSubmissions(); }, [fetchSubmissions]);

  const handleApprove = async (subId) => {
    setApproving(subId);
    try {
      const res = await fetch(`${BACKEND_URL}/api/submissions/${subId}/reviewer-approve`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed');
      }
      fetchSubmissions();
    } catch (err) { alert(err.message); }
    finally { setApproving(null); }
  };

  const pendingApproval = submissions.filter(s => s.status === 'reviewer_review');
  const published = submissions.filter(s => s.status === 'published');

  return (
    <div className="bg-[#F5F7FA] min-h-screen" data-testid="reviewer-dashboard">
      <div className="bg-white border-b border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <p className="section-overline mb-1">Reviewer Portal</p>
          <h1 className="font-serif text-3xl font-semibold text-[#1F1F1F]">
            Welcome, {user?.name}
          </h1>
          <p className="text-[#6B7280] text-sm mt-1">{user?.designation} · {user?.affiliation}</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
          {[
            { label: 'Total Papers', value: submissions.length, icon: <ClipboardList size={18} />, color: 'text-[#1A73E8]', bg: 'bg-blue-50' },
            { label: 'Awaiting Approval', value: pendingApproval.length, icon: <Clock size={18} />, color: 'text-amber-600', bg: 'bg-amber-50' },
            { label: 'Published', value: published.length, icon: <CheckCircle size={18} />, color: 'text-green-600', bg: 'bg-green-50' },
            { label: 'Workflow', value: 'Approve to Publish', icon: <Zap size={18} />, color: 'text-purple-600', bg: 'bg-purple-50', isText: true },
          ].map(stat => (
            <div key={stat.label} className="bg-white border border-[#E5E7EB] p-5">
              <div className={`w-9 h-9 ${stat.bg} flex items-center justify-center mb-3 ${stat.color}`}>{stat.icon}</div>
              <p className={`${stat.isText ? 'text-sm' : 'text-2xl'} font-serif font-bold text-[#1F1F1F]`}>{stat.value}</p>
              <p className="text-xs text-[#6B7280] mt-1">{stat.label}</p>
            </div>
          ))}
        </div>

        {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 text-sm flex items-center gap-2"><AlertCircle size={14} /> {error}</div>}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <div className="bg-white border border-[#E5E7EB]">
              <div className="px-5 py-4 border-b border-[#E5E7EB] bg-amber-50">
                <h2 className="font-semibold text-[#1F1F1F] text-base">Papers Awaiting Your Approval ({pendingApproval.length})</h2>
                <p className="text-xs text-[#6B7280] mt-0.5">These papers have been approved by an editor. Approve to publish.</p>
              </div>
              {loading ? (
                <div className="p-8 text-center"><div className="w-6 h-6 border-2 border-[#1A73E8] border-t-transparent rounded-full animate-spin mx-auto" /></div>
              ) : pendingApproval.length === 0 ? (
                <div className="p-8 text-center text-[#9CA3AF] text-sm">No papers currently awaiting your approval.</div>
              ) : (
                <div className="divide-y divide-[#E5E7EB]">
                  {pendingApproval.map(sub => (
                    <div key={sub.id} className="p-5">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0 cursor-pointer" onClick={() => setExpanded(expanded === sub.id ? null : sub.id)}>
                          <div className="flex items-center gap-2 mb-1">
                            <StatusBadge status={sub.status} />
                            <span className="text-xs text-[#9CA3AF]">{sub.journalTitle}</span>
                          </div>
                          <h3 className="font-semibold text-[#1F1F1F] text-sm leading-snug">{sub.title}</h3>
                          <p className="text-xs text-[#6B7280] mt-1">by {sub.authorName}</p>
                          {sub.editorDecisionByName && (
                            <p className="text-xs text-green-600 mt-1">Editor Approved by: {sub.editorDecisionByName}</p>
                          )}
                          {sub.fileUrl && (
                            <a
                              href={sub.fileUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={e => e.stopPropagation()}
                              className="inline-flex items-center gap-1.5 mt-2 px-3 py-1.5 text-xs font-medium text-[#1A73E8] border border-[#1A73E8] hover:bg-blue-50 transition-colors"
                            >
                              <FileText size={12} />
                              View PDF
                            </a>
                          )}
                          {expanded === sub.id && (
                            <div className="mt-3 space-y-2">
                              <div className="p-3 bg-[#F9FAFB] border border-[#E5E7EB] text-sm text-[#374151]">
                                <p className="text-xs font-semibold text-[#6B7280] uppercase mb-1">Abstract</p>
                                {sub.abstract || 'No abstract provided.'}
                                {sub.keywords && (
                                <p className="text-xs text-[#9CA3AF] mt-2">
                                  Keywords: {(() => { try { return JSON.parse(sub.keywords).join(', '); } catch { return sub.keywords; } })()}
                                </p>
                              )}
                            </div>
                            {sub.coverLetter && (
                              <div className="p-3 bg-blue-50 border border-blue-100 text-sm text-[#374151]">
                                <div className="flex items-center gap-1.5 mb-1.5">
                                  <Mail size={12} className="text-[#1A73E8]" />
                                  <p className="text-xs font-semibold text-[#1A73E8] uppercase">Cover Letter</p>
                                </div>
                                {sub.coverLetter}
                              </div>
                            )}
                          </div>
                        )}
                          <button className="text-xs text-[#1A73E8] mt-2 flex items-center gap-1">
                            {expanded === sub.id ? <><ChevronUp size={12} /> Less</> : <><ChevronDown size={12} /> View Details</>}
                          </button>
                        </div>
                        <button
                          onClick={() => handleApprove(sub.id)}
                          disabled={approving === sub.id}
                          className="flex items-center gap-1.5 px-5 py-2.5 bg-green-600 text-white text-xs font-semibold hover:bg-green-700 disabled:opacity-50 transition-colors flex-shrink-0"
                          data-testid={`publish-${sub.id}`}
                        >
                          <CheckCircle size={14} />
                          {approving === sub.id ? 'Publishing...' : 'Approve & Publish'}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {published.length > 0 && (
              <div className="bg-white border border-[#E5E7EB] mt-6">
                <div className="px-5 py-4 border-b border-[#E5E7EB] bg-green-50">
                  <h2 className="font-semibold text-[#1F1F1F] text-base">Published Papers ({published.length})</h2>
                </div>
                <div className="divide-y divide-[#E5E7EB]">
                  {published.map(sub => (
                    <div key={sub.id} className="px-5 py-3 flex items-center justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-[#1F1F1F] truncate">{sub.title}</p>
                        <p className="text-xs text-[#9CA3AF]">by {sub.authorName} · {sub.journalTitle}</p>
                        {sub.doi && (
                          <a href={sub.zenodoUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-[#1A73E8] hover:underline">
                            DOI: {sub.doi}
                          </a>
                        )}
                      </div>
                      <StatusBadge status="published" />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="space-y-5">
            <div className="bg-white border border-[#E5E7EB]">
              <div className="px-5 py-3 bg-[#F5F7FA] border-b border-[#E5E7EB]">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-[#6B7280]">Publication Workflow</h3>
              </div>
              <div className="p-4 space-y-3">
                {[
                  { step: '1', label: 'Super Admin submits paper', done: true },
                  { step: '2', label: 'Editors assigned (up to 5)', done: true },
                  { step: '3', label: 'Editor approves or rejects', done: true },
                  { step: '4', label: 'Reviewer approves to publish', active: true },
                  { step: '5', label: 'Paper is published', done: false },
                ].map(item => (
                  <div key={item.step} className={`flex items-center gap-2.5 text-sm ${item.active ? 'text-[#1A73E8] font-semibold' : item.done ? 'text-green-600' : 'text-[#9CA3AF]'}`}>
                    <span className={`w-5 h-5 flex items-center justify-center text-xs font-bold border ${item.active ? 'bg-[#1A73E8] text-white border-[#1A73E8]' : item.done ? 'bg-green-50 border-green-200 text-green-600' : 'bg-[#F5F7FA] border-[#E5E7EB]'}`}>
                      {item.done ? '✓' : item.step}
                    </span>
                    {item.label}
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white border border-[#E5E7EB] p-5">
              <div className="flex items-center gap-3 mb-3 pb-3 border-b border-[#E5E7EB]">
                <div className="w-10 h-10 bg-[#174EA6] flex items-center justify-center">
                  <span className="text-white font-semibold text-sm">{user?.name?.charAt(0)}</span>
                </div>
                <div>
                  <p className="text-sm font-semibold text-[#1F1F1F]">{user?.name}</p>
                  <p className="text-xs text-[#6B7280]">Reviewer</p>
                </div>
              </div>
              <p className="text-xs text-[#6B7280]">{user?.email}</p>
              {user?.affiliation && <p className="text-xs text-[#4B5563] mt-1">{user.affiliation}</p>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReviewerDashboard;
